import React, { useEffect, useMemo, useState } from 'react';
import { Form, Checkbox } from 'antd';
import useQueryApiClient, { RequestMethod } from 'utils/useApiClient';
import { useT } from 'hooks/Locale/useT';
import { constructValidationRules } from './validations';

export interface OptionType {
    value: string | number;
    label: React.ReactNode;
}

export interface SimpleCheckboxProps extends React.ComponentProps<typeof Checkbox.Group> {
    options?: OptionType[];
    isFormItem?: boolean;
    name?: string;
    label?: string;
    className?: string;
    validation?: Record<string, any>;
    customRules?: any[];
    url?: string;
    params?: Record<string, any>;
    customLayout?: { labelCol?: object; wrapperCol?: object };
    dataKey?: string;
    valueKey?: string;
    labelKey?: string;
    method?: RequestMethod;
    translation?: string;
}

const SimpleCheckbox: React.FC<SimpleCheckboxProps> = ({
    options,
    isFormItem = true,
    name,
    label,
    className,
    validation = {},
    customRules,
    url,
    params,
    dataKey,
    customLayout,
    valueKey = 'value',
    labelKey = 'label',
    method = 'GET',
    translation = '',
    ...groupProps
}) => {
    const t = useT();
    const [internalOptions, setInternalOptions] = useState<OptionType[]>(options ?? []);

    let labelCol: any = { span: 8 };
    let wrapperCol: any = { span: 6 };

    if (customLayout) {
        labelCol = customLayout.labelCol ?? undefined;
        wrapperCol = customLayout.wrapperCol ?? undefined;
    }

    useEffect(() => {
        if (options) setInternalOptions(options);
    }, [options]);

    const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);
    const shouldFetch = !options && !!url;

    const { fetch, isFetching } = useQueryApiClient({
        request: {
            url: url ?? '',
            method,
            data: params ?? {},
            disableOnMount: true,
        },
        onSuccess: (response): void => {
            const raw = dataKey ? response?.[dataKey] : response;
            let arr: OptionType[] = [];
            if (Array.isArray(raw)) {
                arr = raw.map((item: any) => ({
                    value: item?.[valueKey],
                    label: item?.[labelKey],
                }));
            } else if (raw && typeof raw === 'object') {
                arr = Object.entries(raw).map(([k, v]) => ({ value: k, label: v as any }));
            }
            setInternalOptions(arr);
        },
    });

    useEffect(() => {
        if (!shouldFetch) return;
        fetch(params ?? {});
    }, [shouldFetch, url, method, dataKey, paramsKey]);

    const translationLabel = (value: string): string => {
        if (translation) {
            return t(`${translation}.${value.toLowerCase()}`);
        }
        return value;
    };

    const group = (
        <Checkbox.Group
            className={className}
            disabled={groupProps.disabled || isFetching}
            options={internalOptions.map((opt) => ({
                label: translation ? translationLabel(String(opt.value)) : opt.label ?? String(opt.value),
                value: opt.value,
            }))}
            {...groupProps}
        />
    );

    if (!isFormItem) return group;

    return (
        <Form.Item
            name={name}
            label={t(label!)}
            rules={customRules || constructValidationRules(validation, t)}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
        >
            {group}
        </Form.Item>
    );
};

export default SimpleCheckbox;
